import React, { useState, useRef, useEffect } from 'react';
import { Account, Category, CreateTransactionDTO } from '../../types/budget';
import { Button } from '../ui/Button';
import styles from './TransactionCreation.module.css';

interface TransactionCreationProps {
  categories: Category[];
  accounts: Account[];
  onSave: (dto: CreateTransactionDTO) => void;
  onCancel: () => void;
}

export const TransactionCreation: React.FC<TransactionCreationProps> = ({
  categories,
  accounts,
  onSave,
  onCancel,
}) => {
  const today = new Date().toISOString().split('T')[0];
  
  const [date, setDate] = useState(today);
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [accountId, setAccountId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const descriptionInputRef = useRef<HTMLInputElement>(null);

  // Focus description input on mount
  useEffect(() => {
    descriptionInputRef.current?.focus();
  }, []);

  // Group categories by type
  const expenseCategories = categories.filter(c => c.type === 'expense');
  const incomeCategories = categories.filter(c => c.type === 'income');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const parsedAmount = parseFloat(amount);
    if (isNaN(parsedAmount)) {
      return;
    }

    if (!date || !description.trim()) {
      return;
    }

    // Account is required
    if (!accountId) {
      return;
    }

    try {
      setIsSubmitting(true);

      await onSave({
        date,
        description: description.trim(),
        amount: parsedAmount,
        categoryId,
        accountId,
      });

      // Reset form for next entry
      setDescription('');
      setAmount('');
      descriptionInputRef.current?.focus();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onCancel();
    }
  };

  return (
    <div className={styles.container} onKeyDown={handleKeyDown}>
      <form className={styles.form} onSubmit={handleSubmit}>
        <input
          type="date"
          className={styles.dateInput}
          value={date}
          onChange={(e) => setDate(e.target.value)}
          required
        />

        <input
          ref={descriptionInputRef}
          type="text"
          className={styles.descriptionInput}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Description"
          required
        />

        <input
          type="number"
          step="0.01"
          className={styles.amountInput}
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder="Amount"
          required
        />

        <select
          className={styles.accountSelect}
          value={accountId || ''}
          onChange={(e) => setAccountId(e.target.value || null)}
          required
        >
          <option value="">Select Account *</option>
          {accounts.map((account) => (
            <option key={account.id} value={account.id}>
              {account.name}
            </option>
          ))}
        </select>

        <select
          className={styles.categorySelect}
          value={categoryId || ''}
          onChange={(e) => setCategoryId(e.target.value || null)}
        >
          <option value="">Uncategorized</option>
          {expenseCategories.length > 0 && (
            <optgroup label="Expenses">
              {expenseCategories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </optgroup>
          )}
          {incomeCategories.length > 0 && (
            <optgroup label="Income">
              {incomeCategories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </optgroup>
          )}
        </select>

        <div className={styles.actions}>
          <Button
            variant="secondary"
            size="sm"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            type="submit"
            disabled={isSubmitting || !description.trim() || !amount || !accountId}
            loading={isSubmitting}
          >
            Add
          </Button>
        </div>
      </form>
    </div>
  );
};

export default TransactionCreation;
